/**
 * The fields a pending nurse must still provide on /verifyNurse, as the labels
 * the page lists them under, in the page's order.
 *
 * Proof of license and insurance come from keyon's `has_license_file` and
 * `has_insurance_file` flags (a stored, active file is referenced), never from a
 * file path the page holds.
 *
 * @param {object} form the page's form fields
 * @param {{hasLicenseFile: boolean, hasInsuranceFile: boolean}} flags
 * @returns {string[]} empty when nothing is missing
 */
export function missingVerifyNurseFields(form, { hasLicenseFile, hasInsuranceFile } = {}) {
  const f = form || {};
  const blank = (v) => v === '' || v === null || v === undefined;
  const missing = [];
  if (blank(f.addr)) missing.push('Address');
  if (blank(f.city)) missing.push('City');
  if (blank(f.state)) missing.push('State');
  if (blank(f.zip)) missing.push('Zip');
  if (blank(f.license)) missing.push('License Number');
  if (!hasLicenseFile) missing.push('Proof of License Needed.');
  if (blank(f.licenseSt)) missing.push('License State');
  if (!hasInsuranceFile) missing.push('Proof of Insurance Needed.');
  if (blank(f.dob)) missing.push('DOB');
  if (blank(f.ssn)) missing.push("Social can't be Empty");
  const ssn = String(f.ssn == null ? '' : f.ssn).trim();
  if (ssn.length > 0 && ssn.length !== 4) missing.push('Social Must Be at Least 4 Numbers');
  return missing;
}

/** The ten digits of one US phone number, or null - pacmny-api's UsPhoneNumber::normalize. */
export function tenDigitPhone(value) {
  const withoutExtension = String(value == null ? '' : value).replace(/\s*(x|ext|ext\.|extension|#)\s*\d+\s*$/i, '');
  let digits = withoutExtension.replace(/\D+/g, '');
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);

  return digits.length === 10 ? digits : null;
}

/**
 * The same list for the signed-in /onboarding page (decision D3), in the
 * labels pacmny-api's NurseOnboardingService::missingFields uses, so the page
 * and the server's refusal name the same things.
 *
 * Two differences from the public page: the last four SSN digits are not asked
 * for up front - only once Nursys has said she is not on the list, the one
 * answer that may send them to NCSBN - and a 10-digit phone is required before
 * she continues (ACC-27).
 *
 * @param {object} form the page's form, in the API's field names
 * @param {{hasLicenseFile: boolean, hasInsuranceFile: boolean}} flags
 * @returns {string[]} empty when nothing is missing
 */
export function missingOnboardingFields(form, { hasLicenseFile, hasInsuranceFile } = {}) {
  const f = form || {};
  const blank = (v) => v === null || v === undefined || String(v).trim() === '';
  const missing = [];
  if (blank(f.first_name)) missing.push('First Name');
  if (blank(f.last_name)) missing.push('Last Name');
  if (blank(f.address_line1)) missing.push('Address');
  if (blank(f.city)) missing.push('City');
  if (blank(f.state)) missing.push('State');
  if (blank(f.postal_code)) missing.push('Zip');
  if (blank(f.license_number)) missing.push('License Number');
  if (!hasLicenseFile) missing.push('Proof of License Needed.');
  if (blank(f.license_state)) missing.push('License State');
  if (!hasInsuranceFile) missing.push('Proof of Insurance Needed.');
  if (blank(f.date_of_birth)) missing.push('DOB');
  if (tenDigitPhone(f.phone) === null) missing.push('Phone (10 digits)');
  return missing;
}
